import * as Sentry from '@sentry/ember';

// Attributes every list-item model inherits from `base-item` that describe the
// item itself rather than one particular record. `position` is supplied by the
// caller (the drop decides it), `updated_at` is stripped by the serializer
// anyway and `age_in_days` is derived server-side.
const CARRIED_ATTRIBUTES = [
  'text',
  'text_delta',
  'completed',
  'archived',
  'tags',
  'created_at',
];

// Which relationships each of the five check-in item types actually declares.
// A relationship the target type doesn't have is dropped (a tfo's week has
// nowhere to go on a question); one the source lacks is simply left empty.
const CARRIED_RELATIONSHIPS = {
  question: ['team', 'author'],
  ll: ['team', 'author', 'initiative'],
  wfa: ['team', 'author', 'initiative'],
  uncertainty: ['team', 'author', 'initiative'],
  tfo: ['team', 'author', 'initiative', 'workstream', 'week'],
};

export function isConvertibleType(type) {
  return Object.prototype.hasOwnProperty.call(CARRIED_RELATIONSHIPS, type);
}

// Async relationships read synchronously — the drop handler runs in the middle
// of a DOM gesture and must not await.
function peekBelongsTo(record, name) {
  try {
    return record.belongsTo(name)?.value?.() ?? null;
  } catch {
    return null;
  }
}

function peekHasMany(record, name) {
  try {
    return record.hasMany(name)?.value?.() ?? null;
  } catch {
    return null;
  }
}

function attributesOf(source) {
  return CARRIED_ATTRIBUTES.reduce((attrs, key) => {
    const value = source[key];
    if (value !== undefined) attrs[key] = value;
    return attrs;
  }, {});
}

function relationshipsOf(source, targetType) {
  const sourceType = source.constructor.modelName;
  const sourceRelationships = CARRIED_RELATIONSHIPS[sourceType] ?? [];

  return (CARRIED_RELATIONSHIPS[targetType] ?? [])
    .filter((name) => sourceRelationships.includes(name))
    .reduce((rels, name) => {
      const related = peekBelongsTo(source, name);
      if (related) rels[name] = related;
      return rels;
    }, {});
}

// Best effort: the move is the user's intent, so a comment thread that refuses
// to follow is reported rather than allowed to abort the move.
async function reparentComments(comments, sourceType, target) {
  const targetType = target.constructor.modelName;

  for (const comment of comments) {
    if (comment.isNew || comment.isDeleted) continue;
    try {
      comment[sourceType] = null;
      comment[targetType] = target;
      await comment.save();
    } catch (error) {
      Sentry.captureException(error);
    }
  }
}

async function loadComments(source) {
  try {
    return ((await source.comments) ?? []).slice();
  } catch (error) {
    Sentry.captureException(error);
    return [];
  }
}

/**
 * Move a check-in list item to a different item type.
 *
 * The five types live on five different endpoints, so there is no atomic move:
 * a new record is created in the target list and the source is deleted. The
 * source is removed from the store *first* so it leaves its old section
 * immediately, but its DELETE is only committed once the new record has saved —
 * that way a failed create rolls straight back to the item the user still has.
 *
 * @param {Store} store
 * @param {Model} source item being moved
 * @param {string} targetType model name of the list it was dropped into
 * @param {object} overrides `position`, and `assignees` when the target list
 *   groups by assignee and the drop named a person
 * @returns {Promise<Model>} the newly created record
 */
export async function convertListItem(
  store,
  source,
  targetType,
  overrides = {},
) {
  const sourceType = source.constructor.modelName;
  // Read everything off the source while it is still a live record.
  const attributes = attributesOf(source);
  const relationships = relationshipsOf(source, targetType);
  const assignees =
    overrides.assignees ?? (peekHasMany(source, 'assignees') ?? []).slice();
  // Kicked off — not awaited — while the source is still live: awaiting here
  // would leave the item out of both sections for the length of the fetch,
  // since dragula's node has already been pulled out of the DOM.
  const comments = loadComments(source);

  const created = store.createRecord(targetType, {
    ...attributes,
    ...relationships,
    ...overrides,
    assignees,
  });

  // Local-only delete: the item leaves its old section now, and can be put back
  // with rollbackAttributes() if the create never lands.
  source.deleteRecord();

  try {
    await created.save();
  } catch (error) {
    source.rollbackAttributes();
    created.rollbackAttributes();
    throw error;
  }

  await reparentComments(await comments, sourceType, created);
  await source.save();

  return created;
}
